<template>
  <div
    @mouseenter="$emit('hovered')"
    @mouseleave="$emit('unhovered')"
    class="product-card"
    :class="{
      'product-card--not--adaptive': notAdaptive,
      'product-card--open': opened,
      'product-card--slider': slider,
      'product-card--is-touch': isTouch,
      'product-card--adaptive-main-page': adaptiveMainPage,
      'product-card--simple-card': simpleCard
    }"
    :data-productKey="code"
  >
    <div
      ref="productCard"
      class="product-card__full">
      <div class="product-card__info">
        <div class="product-card__height">
          <div class="product-card__photo-wrapper">
            <ProductPhoto
              class="product-card__photo"
              ref="productPhoto"
              @click="handleRedirect"
              :product-id="id"
              :link="link"
              :mobileImageUrl="mobileImageUrl"
              :imageUrl="imageUrl"
              :secondImageUrl="secondImageUrl"
              :title="title"
              :progressbar-current="progressbarCurrent"
              :progressbar-total="progressbarTotal"
            />
            <ProductBottomBadges
              :data="bottomBadge"
              class="product-card__bottom-badges" />
          </div>
          <ProductTitle
            @click="handleRedirect"
            :title="title"
            :link="link"
            class="product-card__title" />
          <div class="product-card__reviews-wrap">
            <div class="product-card__reviews">
              <StarRating
                v-if="rating"
                :value="rating"
                :disabled="true"
                :mobile-transform="true"
                class="product-card__rating"
                :title="Number(rating).toFixed(1)"
              />
              <ReviewsCounter
                :product-link="link"
                :value="reviewsCount" />
            </div>
            <ProductCode
              :code="code"
              class="product-card__code"
              :class="{ 'product-card__code--hidden': !$t('product_code') }"
              is-adaptive
            />
          </div>
          <span
            class="product-card__arrival-date-employee"
            v-if="arrivalDateEmployee && isManager"
            v-html="arrivalDateEmployee"
          ></span>
        </div>
        <div class="product-card__price-actions">
          <ProductPrice
            :type="adaptiveMainPage ? 'adaptive-main-page' : simpleCard ? 'simple-card' : null"
            v-if="showProductPrice"
            :price="getNewPrice"
            :old-price="getOldPrice"
            class="product-card__price"
          />
          <span
            v-else-if="!isPreorder"
            class="product-card__price product-card__price--not-available"
            :class="{ 'product-card__price--not-available-arrival': arrivalDate }"
          >{{ arrivalDate || $t('out_of_stock') }}</span
          >
          <span
            v-else
            class="product-card__price product-card__price--pre-order--available">{{
              arrivalDate || ''
            }}</span>
          <BuyBtn
            v-if="isBuyButtonVisible && adaptiveMainPage || simpleCard"
            :title-ru="titleRu"
            :product-id="id"
            :code="code"
            :price="getNewPrice"
            :store-id="availableInStore ? availableInStore.store_id : ''"
            :esputnik-data="esputnikData"
            :esputnik-block-id="esutnikBlockId"
            :product-link="link"
            class="product-card__buy-btn"
            type="icon-only"
          />
        </div>
        <div class="product-card__actions">
          <a
            class="product-card__pseudo-btn"
            v-if="withoutBuy"
            :href="link">
            {{ $t('product_show_detail_title') }}
          </a>
          <BuyBtn
            v-else-if="isBuyButtonVisible"
            :title-ru="titleRu"
            :product-id="id"
            :code="code"
            :price="getNewPrice"
            :store-id="availableInStore ? availableInStore.store_id : ''"
            :esputnik-data="esputnikData"
            :esputnik-block-id="esutnikBlockId"
            :product-link="link"
            class="product-card__buy-btn"
          />
          <BuyBtn
            v-else-if="isPreorder"
            pre-order-btn
            :title-ru="titleRu"
            :title="title"
            :price-old="getOldPrice"
            :photo="imageUrl"
            :product-id="id"
            :code="code"
            :price="getNewPrice"
            :product-link="link"
            class="product-card__buy-btn"
            :class="{ 'product-card__buy-btn--novelty': isPreorder }"
          />
          <WaitListBtn
            v-else
            :in-wait-list-status="isInWaitList"
            :title-ru="titleRu"
            :product-id="id"
            :is-small-card="true"
            class="wait-list--card"
          />
          <WishListBtn
            class="product-card__wish-list-btn"
            :id="id"
            :price="getNewPrice"
            :analytics-data="analyticsData"
            :code="code"
            :is-in-stock="isAvailable"
            :in-wish-list-status="isInWishList"
            @mouseenter.native="handlePhoto($event, true)"
            @mouseleave.native="handlePhoto($event, false)"
          />
          <AddToComparisonBtn
            :product-id="id"
            :title-ru="titleRu"
            class="product-card__add-to-comparison"
            @mouseenter.native="handlePhoto($event, true)"
            @mouseleave.native="handlePhoto($event, false)"
          />
        </div>
        <ProductOptions
          class="product-card__options"
          :link="link"
          v-if="options.length"
          is-product-card
          :options="options" />
        <ProductCardCharacteristics
          v-if="characteristics.length"
          :values="characteristics ? characteristics : []"
          class="product-card__characteristics"
        />
      </div>
      <ProductBadges
        v-if="computedBadges.length"
        class="product-card__badges"
        :class="{ 'product-badges--not--adaptive': notAdaptive }"
        :values="computedBadges"
      />
    </div>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import { mapGetters, mapState } from 'vuex'
import { isTouch } from '../../../mixins/isTouchDevice'
import ProductPrice from '../ProductPrice'
import StarRating from '../StarRating'
import ReviewsCounter from '../ReviewsCounter'
import WishListBtn from '../WishListBtn'
import ProductCardCharacteristics from '../ProductCardCharacteristics'
import ProductBadges from '../ProductBadges'
import LazyLoadDirective from '../../../directives/LazyLoadDirective'
import ProductOptions from '../product-options/ProductOptions'
import ProductPhoto from '../ProductPhoto'
import ProductTitle from '../ProductTitle'
import ProductCode from '../ProductCode'
import BuyBtn from '../../cart/BuyBtn'
import AddToComparisonBtn from '../product-comparison/AddToComparisonBtn'
import WaitListBtn from '../product-page/WaitListBtn'
import ProductBottomBadges from '../ProductBottomBadges'
import { GoogleTagManagerService, GoogleEnhancedEcommerce } from '@/services/AnalyticService'

export default {
  name: 'ProductCard',
  mixins: [isTouch],
  components: {
    ProductTitle,
    ProductPhoto,
    ProductOptions,
    ProductPrice,
    StarRating,
    ReviewsCounter,
    WishListBtn,
    ProductCardCharacteristics,
    ProductBadges,
    ProductCode,
    BuyBtn,
    AddToComparisonBtn,
    WaitListBtn,
    ProductBottomBadges
  },
  props: {
    categoryBlockTitle: {
      type: String,
      default: ''
    },
    categoryBlockId: {
      type: [Number, String],
      default: null
    },
    position: {
      type: Number,
      default: null
    },
    id: {
      type: Number,
      default: null
    },
    code: {
      type: String,
      default: ''
    },
    imageUrl: {
      type: String,
      required: false,
      default: ''
    },
    secondImageUrl: {
      type: String,
      required: false,
      default: ''
    },
    mobileImageUrl: {
      type: String,
      default: ''
    },
    link: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    titleRu: {
      type: String,
      default: null
    },
    categoryId: {
      type: Number,
      default: null
    },
    categoryParentId: {
      type: Number,
      default: null
    },
    rating: {
      type: String,
      default: ''
    },
    reviewsCount: {
      type: Number,
      default: null
    },
    price: {
      type: String,
      default: ''
    },
    characteristics: {
      type: Array
    },
    badges: {
      type: String,
      default: ''
    },
    isAvailable: {
      type: Boolean,
      default: false
    },
    isPreorder: {
      type: Boolean,
      default: false
    },
    isNovelty: {
      type: Boolean,
      defaultL: false
    },
    isInWaitList: {
      type: Boolean,
      default: false
    },
    isInWishList: {
      type: Boolean,
      default: false
    },
    notAdaptive: {
      type: Boolean,
      default: false
    },
    adaptiveMainPage: {
      type: Boolean,
      default: false
    },
    opened: {
      type: Boolean,
      default: false
    },
    slider: {
      type: Boolean,
      default: false
    },
    block: {
      type: String,
      default: ''
    },
    simpleCard: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      default: () => []
    },
    bottomBadge: {
      type: Array,
      default: () => []
    },
    withoutBuy: {
      type: Boolean,
      default: false
    },
    inStock: {
      type: Boolean,
      default: undefined
    },
    arrivalDate: [String, Boolean],
    arrivalDateEmployee: [String, Boolean],
    has360: {
      type: Boolean,
      default: false
    },
    analyticsData: {
      type: Array,
      default: () => []
    },
    availableInStore: [Boolean, Object],
    esputnikData: Object,
    esutnikBlockId: String,
    progressbarTotal: {
      type: Number,
      default: 0
    },
    progressbarCurrent: {
      type: Number,
      default: 0
    }
  },
  methods: {
    async handleRedirect (event) {
      const { e, link } = event
      e.preventDefault()

      const analyticsData = this.analyticsData.map(item => {
        if (item.hasOwnProperty('quantity')) delete item.quantity

        return { ...item, position: this.position }
      })

      for (let item of analyticsData) {
        if (!item.id) {
          Sentry.captureMessage('Empty analytics data item', {
            extra: { analyticsData },
            tags: { category: 'analytics' }
          })
        }
      }
      if (GoogleEnhancedEcommerce.isEnabled) {
        await GoogleEnhancedEcommerce.selectItem({
          'items': [GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.analyticsData[0])]
        })
      } else {
        await GoogleTagManagerService.productClickEvent({
          analyticsData,
          actionField: {
            'list': this.list || ''
          }
        })
      }
      location.href = link
    },
    handlePhoto (e, isEnter) {
      if (this.$refs.productPhoto) {
        const { setSecondaryPhoto, setMainPhoto } = this.$refs.productPhoto
        if (isEnter) setSecondaryPhoto()
        else setMainPhoto()
      }
    },
    checkIfProductFromBannerLink () {
      const savedBanners = JSON.parse(localStorage.getItem('clickedBanners')) || []
      const currPathWithoutLang = window.location.pathname.replace('/uk/', '/')
      const currBanner = savedBanners.find(el => el.banner_link.includes(currPathWithoutLang))
      if (currBanner) {
        const bannerPromoItems = JSON.parse(localStorage.getItem('bannerPromoItems')) || {}
        if (!Array.isArray(bannerPromoItems[currBanner.promotion_id])) {
          bannerPromoItems[currBanner.promotion_id] = []
        }
        if (!bannerPromoItems[currBanner.promotion_id].find(el => el.toString() === this.computedCode.toString())) {
          bannerPromoItems[currBanner.promotion_id].push(this.computedCode)
        }
        localStorage.setItem('bannerPromoItems', JSON.stringify(bannerPromoItems))
      }
    }
  },
  computed: {
    ...mapState({
      config: state => state.init.config
    }),
    ...mapGetters(['isManager']),
    // TODO: УДАЛИТЬ!
    costilForCheckSale () {
      return (this.code?.includes('-') || ([ 704, 1380, 1381, 1382, 1699, 3857, 4138, 4139, 4140, 4141, 4142, 4143, 4144,
        4145, 4146, 4147, 4148, 4149, 4150, 4151, 4152, 4153, 4154, 4386, 4387, 4430, 4431, 4432, 4433, 4434, 4435, 4436,
        4437, 4438, 4439, 4440, 4441, 4517, 4518, 4519, 4520, 4521, 4522, 4523, 4524, 4525, 4526, 4527, 4528, 4931, 5090,
        5915, 5956, 6298, 6742, 6743, 6744, 6745, 6746, 6747, 6789, 6863, 6864, 6865, 6866, 6867, 6871, 6873, 6874, 6875,
        6876, 6877, 6878, 6880, 6882, 6883, 6884, 6885, 6900, 7022, 7042, 7043, 7044, 7232, 7233, 7327, 7431, 7432, 7433,
        7434, 7435, 7436, 7437, 7438, 7439
      ].includes(this.id))) ?? false
    },
    dataForAnalytics () {
      const price = JSON.parse(this.price)
      const discount = parseFloat(price.price_old) - parseFloat(price.price_new)
      return {
        ...this.analyticsData[0],
        item_name: this.titleAnalytics || this.title,
        discount: discount.toFixed(2),
        index: this.position,
        item_list_name: this.block || ''
      }
    },
    computedCode () {
      if (this.analyticsData.length > 1) {
        return this.analyticsData[0].id
      }
      return this.analyticsData[0].id || this.code
    },
    computedBadges () {
      // Если активны счастливые часы, переопределяем бейджи
      if (this.config?.['happy_hours']?.['is_in_time'] && this.config?.['happy_hours']?.['sale_rate'] && !this.costilForCheckSale) {
        return [{
          name: '-' + this.config.happy_hours.sale_rate + '% 🕓',
          color: '#4e93d9'
        }]
      }

      return this.badges ? JSON.parse(this.badges) : []
    },
    priceObject: function () {
      return JSON.parse(this.price)
    },
    productCardWidth () {
      return this.$refs.productCard.getBoundingClientRect()
    },
    list () {
      let list

      if (window.translationParams.route.includes('site/index') && this.block === 'sale') {
        list = 'Sale Main Page'
      } else if (window.translationParams.route.includes('shop/catalog/sale')) {
        list = 'Sale Page'
      } else if (window.translationParams.route.includes('shop/catalog/novelty')) {
        list = 'New Products Page'
      } else if (window.translationParams.route.includes('shop/catalog/search')) {
        list = 'Search Page'
      } else if (window.translationParams.route.includes('site/index') && this.block === 'novelty') {
        list = 'New Products Main Page'
      } else if (window.translationParams.route.includes('shop/catalog/category')) {
        list = 'Category Page'
      } else if (window.translationParams.route.includes('shop/catalog/product') && this.block === 'up_sale') {
        list = 'Upsale Product Page'
      } else if (window.translationParams.route.includes('shop/catalog/outlet')) {
        list = 'Outlet Page'
      } else if (this.block === 'bytogether') {
        list = 's-etim-tovarom-pokupayut'
      } else if (this.block === 'bytogethercart') {
        list = 's-etim-tovarom-pokupayut-korzina'
      } else if (this.block) {
        list = this.block
      }
      return list
    },
    getNewPrice () {
      return +this.priceObject.price_new ?? null
    },
    getOldPrice () {
      return this.priceObject.price_old && this.priceObject.price_old !== this.priceObject.price_new
        ? +this.priceObject.price_old
        : null
    },
    showProductPrice () {
      return (this.isAvailable && this.getNewPrice) || (this.availableInStore && this.getNewPrice) || (this.isPreorder && this.getNewPrice)
    },
    isBuyButtonVisible () {
      // параметр inStock на данный момент (21.01.22) прокидывается только в вишлисте.
      if (this.isPreorder || (this.inStock !== undefined && this.inStock === false && !this.availableInStore)) {
        return false
      }
      return this.priceObject.price_new && !this.withoutBuy && (this.isAvailable || this.availableInStore)
    },
    parentCategoryCode () {
      if (this.analyticsData.length) {
        const mainProduct = this.analyticsData.find(el => el.is_main_product)
        if (mainProduct) {
          return mainProduct.id
        }
        return this.analyticsData[0].id
      }
      return this.code
    }
  },
  directives: {
    lazyload: LazyLoadDirective
  },
  mounted () {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.checkIfProductFromBannerLink()
          if (this.$parent && this.$parent.collectAnalyticsData) {
            if (GoogleEnhancedEcommerce.isEnabled) {
              let GEEdata = this.analyticsData[0]
              if (this.categoryBlockTitle && this.categoryBlockId) {
                GEEdata = {
                  ...GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.analyticsData[0]),
                  item_list_name: this.categoryBlockTitle,
                  item_list_id: this.categoryBlockId
                }
              }
              this.$parent.collectAnalyticsData(GEEdata)
            } else {
              this.$parent.collectAnalyticsData(this.dataForAnalytics)
            }
          }
          const analyticsData = this.analyticsData.map(item => {
            if (item.hasOwnProperty('quantity')) delete item.quantity

            return { ...item, position: this.position, list: this.list || '' }
          })

          if (GoogleEnhancedEcommerce.isEnabled) {
            this.$emit('onIntersecting', GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.analyticsData[0]))
          } else {
            this.$emit('onIntersecting', this.dataForAnalytics)
            GoogleTagManagerService.productViewEvent({ analyticsData })
          }

          // observer.unobserve(this.$refs.productCard)
        }
      },
      { threshold: 0.2 }
    )
    observer.observe(this.$refs.productCard)
    // setTimeout(() => {
    //   observer.observe(this.$refs.productCard)
    // }, 0)
  }
}
</script>

<style lang="sass" scoped>

.product-card
  position: relative
  margin-top: 20px
  padding-bottom: 15px
  border-bottom: 1px solid #ededed

  +mmin($tablet)
    height: 355px
    margin-top: 0
    padding-bottom: 0
    overflow: hidden
    border-bottom: 0

    &:hover
      overflow: visible

    &:hover .product-card__wish-list-btn,
    &:hover .product-card__add-to-comparison
      display: block

    &:hover .product-card__full
      position: relative
      z-index: 3

    &:hover .product-card__full:before
      box-shadow: 0 3px 15px rgba(0, 0, 0, 0.15)

.special-offer .owl-item:not(.active) .product-card,
.swiper-item:not(.swiper-slide-visible) .product-card,
.swiper-slide-prev .product-card

  +mmin($tablet)
    pointer-events: none

.product-card--not--adaptive
  height: 355px
  margin-top: 0
  padding-bottom: 0
  border-bottom: 0

.product-card--open
  height: auto

.product-card--slider
  height: auto

  +mmin($tablet)
    height: 355px

.product-card--is-touch
  +mmin($tablet)
    height: 410px

.product-card__full
  background-color: $page-bg

  +mmin($tablet)
    padding: 20px

    &:before
      content: ''
      position: absolute
      left: -1px
      right: -1px
      top: -1px
      bottom: 0
      background-color: $page-bg
      border-radius: 3px
      z-index: -1

.product-card--not--adaptive .product-card__full
  padding: 10px
  +mmin($tablet)
    padding: 20px

.product-card__info
  min-height: 210px
  display: flex
  flex-flow: column wrap

.product-card--not--adaptive .product-card__info
  min-height: 355px
  margin-left: 0

.product-card__height
  margin-left: 130px
  order: -1

  +mmin($tablet)
    min-height: 290px
    margin-left: 0
    order: 0

.product-card--not--adaptive .product-card__height
  min-height: 290px
  margin-left: 0
  order: 0

  +mmin($tablet)
    min-height: 290px

.product-card__reviews-wrap,
.product-card__reviews,
.product-card__price-actions
  display: flex
  align-items: center

.product-card__reviews-wrap,
.product-card__price-actions
  flex-flow: row wrap
  justify-content: space-between

.product-card__price-actions .product-card__buy-btn
  +mmin($tablet)
    display: none

.product-card__reviews-wrap
  position: absolute
  left: 0
  top: 130px
  max-width: 120px

  +mmin($tablet)
    position: static
    max-width: 100%

.product-card--not--adaptive .product-card__reviews-wrap
  position: static
  max-width: 100%

.product-card__reviews
  align-items: center
  margin-bottom: 10px

  +mmin($tablet)
    margin-top: 5px
    margin-bottom: 0

.product-card__rating
  margin-right: 36px

  +mmin($tablet)
    margin-right: 10px

.product-card--not--adaptive .product-card__reviews
  position: static
  margin-bottom: 0

.product-card__price-actions
  min-height: 34px
  align-items: flex-end
  margin-bottom: 15px
  margin-left: 130px

  +mmin($tablet)
    margin-top: auto
    margin-left: 0
    align-items: center

.product-card--not--adaptive .product-card__price-actions
  margin-top: auto
  margin-left: 0

.product-card__price
  //margin: 5px 0 0

  +mmin($tablet)
    //margin: 5px 0

.product-card--not--adaptive:not(.product-card--adaptive-main-page):not(.product-card--simple-card) .product-card__price
  display: block
  margin: 5px 0

.product-card__price--not-available
  color: $placeholder-color
  font-weight: 500
  font-size: 16px
  line-height: 19px
  padding: 11px 0

  +mmin($tablet)
    font-size: 16px
    line-height: 21px

.cart .product-card--not--adaptive .product-card__price--not-available
  font-size: 16px
.product-card--not--adaptive .product-card__price--not-available
  font-size: 16px
  line-height: 21px

.product-card__price--not-available-arrival
  font-size: 16px

.product-card__price--pre-order--available
  color: $placeholder-color
  font-weight: 500
  font-size: 16px
  line-height: 19px

.product-card__arrival-date-employee
  display: block
  font-size: 12px
  line-height: 15px
  color: $main-color

.product-card__actions
  display: flex
  flex-flow: row-reverse nowrap
  align-items: center
  margin-top: auto
  justify-content: space-around

  +mmin($tablet)
    justify-content: flex-start
    flex-flow: row nowrap

.product-card__pseudo-btn
  display: flex
  align-items: center
  padding: 6px 36px
  max-height: 40px
  background-color: $main-color
  border: 1px solid $main-color
  border-radius: 3px
  color: #fff
  font-weight: 500
  font-size: 14px
  line-height: normal
  transition: background-color .1s ease-in-out,color .1s ease-in-out

  &:hover
    background-color: $main-color--hover

.product-card__actions + .product-card__characteristics
  +mmin($tablet)
    margin-top: 20px

.product-card--not--adaptive .product-card__actions
  justify-content: flex-start
  flex-flow: row nowrap

.product-card__characteristics
  order: -1
  margin-left: 130px
  margin-bottom: 10px

  +mmin($tablet)
    order: 0
    margin-left: 0
    margin-bottom: 0

.product-card--not--adaptive .product-card__characteristics
  display: none

  +mmin($tablet)
    display: block
    order: 0
    margin-left: 0

.wait-list
  display: none

  +mmin($tablet)
    display: block

.product-card--not--adaptive
  .wait-list
    display: block

.product-card__badges
  position: absolute
  top: 0
  left: 0

  +mmin($tablet)
    top: 20px
    left: 20px

.product-card--not--adaptive .product-card__badges
  top: 20px
  left: 20px

.product-card__wish-list-btn,
.product-card__add-to-comparison

  +mmin($tablet)
    display: none
    position: absolute
    right: 20px

.product-card__add-to-comparison
  +mmin($tablet)
    top: 20px

.product-card__wish-list-btn
  +mmin($tablet)
    top: 60px

.product-card--not--adaptive
  .product-card__add-to-comparison,
  .product-card__wish-list-btn
    display: block
    position: absolute
    right: 20px

    +mmin($tablet)
      display: none

  .product-card__add-to-comparison
    top: 20px

  .product-card__wish-list-btn
    top: 60px

+mmin($tablet)
  .product-card__photo-wrapper
    position: relative

.product-card--adaptive-main-page
  .product-card__photo-wrapper
    +mmax($tablet)
      position: relative
      padding-top: 145px

  .product-card__photo
    +mmax($tablet)
      position: absolute
      left: 0
      top: 0
      width: 100%

.product-card__bottom-badges
  +mmin($tablet)
    position: absolute
    bottom: 0
    left: 0
    pointer-events: none

.product-card--is-touch
  .product-card__add-to-comparison,
  .product-card__wish-list-btn

    +mmin($tablet)
      display: block

.product-card--not--adaptive .product-card__bottom-badges
  position: absolute
  bottom: 0
  left: 0
  pointer-events: none

.product-card__code--hidden
  opacity: 0

.product-card--adaptive-main-page
  +mmax(768)
    height: auto

    .product-card__pseudo-btn
      display: none

    .product-card__height
      width: 100%
      min-height: auto

    .product-card__info
      min-height: auto

    .product-card__bottom-badges
      display: none

    .product-card__code
      display: none

    .product-card__options
      display: none

    .product-card__characteristics
      display: none

    .product-card__actions
      .product-card__buy-btn
        display: none

      .wait-list--card
        display: none

      .product-card__add-to-comparison
        top: 10px
        right: 10px

      .product-card__wish-list-btn
        top: 50px
        right: 10px

    .product-card__price-actions
      width: 100%
      align-items: center
      margin: 15px 0 0

    .product-card__badges
      //display: none
      top: 10px
      left: 10px

.product-card--simple-card
  height: auto

  &:hover
    overflow: hidden

    .product-card__full
      position: initial
      z-index: 0

  .product-card__pseudo-btn
    display: none

  .product-card__full
    border: 1px solid $nd-border-color
    border-radius: 5px
    padding: 15px 10px

    &:hover:before
      box-shadow: none

  .product-card__photo
    width: 100%
    max-width: 140px
    margin: 0 auto
    min-height: 150px

    +mmin($tablet)
      max-width: 169px
      min-height: 205px

  .product-card__height
    width: 100%
    min-height: auto

  .product-card__info
    min-height: auto

  .product-card__bottom-badges
    display: none

  .product-card__code
    display: none

  .product-card__options
    display: none

  .product-card__characteristics
    display: none

  .product-card__actions
    .product-card__buy-btn
      display: none

    .wait-list--card
      display: none

    .product-card__add-to-comparison
      top: 10px
      right: 10px

    .product-card__wish-list-btn
      top: 50px
      right: 10px

  .product-card__price-actions
    width: 100%
    align-items: center
    margin: 15px 0 0

    .product-card__buy-btn
      display: block

  .product-card__badges
    //display: none
    top: 10px
    left: 10px

  .product-card__title
    min-height: 35px
</style>
